import { RefObject, useEffect, useState } from 'react';
import { pointerEngine, PlayGesture } from './pointerEngine';
import { playLoop } from './playLoop';
import { PRESETS, stepSpring, settled, Spring } from './springs';
import { isProtectedElement } from './protect';
import { useStorm } from '../storm/StormContext';
import { mayGrab } from './panGate';

// Toss tile: drag a photo thumbnail and it follows the pointer (snappy springs); release and it
// springs back to its grid slot (bouncy), keeping the release velocity so a flick overshoots.
// Long-press wiggles it (±WIGGLE_DEG for WIGGLE_MS) and reveals its ✕ until the next press
// elsewhere. Only transform moves — grid order never changes. Drags starting on a button (the
// ✕) are ignored. Reduced motion: no toss, no wiggle; long-press still reveals ✕.
const WIGGLE_DEG = 3;
const WIGGLE_MS = 400;
const WIGGLE_HZ = 10;
const MAX_V = 3000; // px/s
const EPS = 0.05;

const clamp = (v: number, m: number) => Math.max(-m, Math.min(m, v));

export function useTossTile(tileRef: RefObject<HTMLElement>): { revealed: boolean } {
  const { reducedMotion } = useStorm();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = tileRef.current;
    if (!el || isProtectedElement(el)) return;
    const calm = reducedMotion;
    let x: Spring = { x: 0, v: 0 }, y: Spring = { x: 0, v: 0 };
    let tx = 0, ty = 0, rot = 0;
    let wx = 0, wy = 0, wr = 0;
    let armed = false, grabbing = false;
    let wiggleAt = -1;
    let pid = -1;
    let unreg: (() => void) | null = null;

    const inside = (t: Element | null) => !!t && el.contains(t);
    const write = () => {
      if (Math.abs(x.x - wx) < EPS && Math.abs(y.x - wy) < EPS && Math.abs(rot - wr) < 0.01) return;
      wx = x.x; wy = y.x; wr = rot;
      const rest = Math.abs(wx) < EPS && Math.abs(wy) < EPS && Math.abs(wr) < 0.01;
      el.style.transform = rest ? '' : `translate3d(${wx.toFixed(2)}px,${wy.toFixed(2)}px,0) rotate(${wr.toFixed(3)}deg)`;
    };
    const tick = (dt: number, now: number) => {
      const p = grabbing ? PRESETS.snappy : PRESETS.bouncy;
      x = stepSpring(x, tx, dt, p);
      y = stepSpring(y, ty, dt, p);
      const age = wiggleAt < 0 ? Infinity : now - wiggleAt;
      if (age < WIGGLE_MS) rot = WIGGLE_DEG * Math.sin((age / 1000) * WIGGLE_HZ * 2 * Math.PI) * (1 - age / WIGGLE_MS);
      else { rot = 0; wiggleAt = -1; }
      const done = !grabbing && wiggleAt < 0 && settled(x, tx, 0.05) && settled(y, ty, 0.05);
      if (done) { x = { x: tx, v: 0 }; y = { x: ty, v: 0 }; }
      write();
      if (done && unreg) {
        unreg(); unreg = null;
        el.style.zIndex = '';
      }
    };
    const wake = () => { if (!unreg) unreg = playLoop.register(tick); };

    let ptype = 'mouse';
    let cancelled = false;
    const onNativeDown = (e: PointerEvent) => { pid = e.pointerId; ptype = e.pointerType; cancelled = false; };
    // The browser took the gesture for native scroll (pan-y handle, vertical drag): never grab it.
    const onNativeCancel = () => { cancelled = true; if (grabbing) release(0, 0); };
    // Touch long-press on an image would open the OS image menu; suppress only for touch.
    const onContext = (e: Event) => { if (pointerEngine.state.type === 'touch') e.preventDefault(); };
    el.addEventListener('pointerdown', onNativeDown, { passive: true });
    el.addEventListener('pointercancel', onNativeCancel, { passive: true });
    el.addEventListener('contextmenu', onContext);

    const onDown = (e: PlayGesture) => {
      armed = inside(e.target) && !(e.target && e.target.closest('button'));
      if (!inside(e.target)) setRevealed(false);
    };
    const onDragStart = (e: PlayGesture) => {
      if (!armed || calm) return;
      const s = pointerEngine.state;
      if (!mayGrab(ptype, getComputedStyle(el).touchAction, e.x - s.downX, e.y - s.downY, cancelled)) return;
      grabbing = true;
      wiggleAt = -1; rot = 0;
      el.style.zIndex = '5';
      try { if (pid !== -1) el.setPointerCapture(pid); } catch { /* pointer already gone */ }
      wake();
    };
    const onDrag = (e: PlayGesture) => {
      if (!grabbing) return;
      const s = pointerEngine.state;
      tx = e.x - s.downX; ty = e.y - s.downY;
      wake();
    };
    const release = (vx: number, vy: number) => {
      grabbing = false;
      tx = 0; ty = 0;
      // Keep the throw: release velocity (px/ms → px/s) carries the tile past its slot.
      x = { x: x.x, v: clamp(vx * 1000, MAX_V) };
      y = { x: y.x, v: clamp(vy * 1000, MAX_V) };
      wake();
    };
    const onDragEnd = (e: PlayGesture) => { if (grabbing) release(e.vx, e.vy); };
    const onLong = (e: PlayGesture) => {
      if (!inside(e.target) || (e.target && e.target.closest('button'))) return;
      setRevealed(true);
      if (calm) return;
      wiggleAt = performance.now();
      wake();
    };
    const onUp = () => { armed = false; pid = -1; };
    const offs = [
      pointerEngine.on('down', onDown), pointerEngine.on('dragstart', onDragStart), pointerEngine.on('drag', onDrag),
      pointerEngine.on('dragend', onDragEnd), pointerEngine.on('longpress', onLong), pointerEngine.on('up', onUp),
    ];
    return () => {
      offs.forEach((o) => o());
      el.removeEventListener('pointerdown', onNativeDown);
      el.removeEventListener('pointercancel', onNativeCancel);
      el.removeEventListener('contextmenu', onContext);
      if (unreg) unreg();
      el.style.transform = '';
      el.style.zIndex = '';
    };
  }, [tileRef, reducedMotion]);

  return { revealed };
}
